import { Component } from '@theme/component'

const MIN_THUMB_HEIGHT = 56
const GRABBING_CLASS = 'ui-scrollbar-grabbing'

function clamp(min, max, value) {
  return Math.max(min, Math.min(max, value))
}

const isCoarsePointer = () =>
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(pointer: coarse)').matches

class ScrollbarElement extends Component {
  requiredRefs = ['thumb']
  metrics = null
  metricsFrame = null
  positionFrame = null
  resizeObserver = null
  drag = null
  lastDisabled = null
  lastThumbHeight = null
  lastThumbOffset = null
  isInteractive = false

  connectedCallback() {
    super.connectedCallback()

    this.isInteractive = !isCoarsePointer()
    this.refreshScrollbarState()
    this.observeScrollbarMetrics()

    if (this.isInteractive) {
      this.refs.thumb.addEventListener('pointerdown', this.onPointerDown, false)
      window.addEventListener('pointermove', this.onPointerMove, false)
      window.addEventListener('pointerup', this.onPointerUp, false)
    }

    window.addEventListener('scroll', this.onScroll, { passive: true })
    window.addEventListener('resize', this.onResize, { passive: true })
    window.visualViewport?.addEventListener('resize', this.onResize, {
      passive: true
    })
  }

  disconnectedCallback() {
    super.disconnectedCallback()

    if (this.isInteractive) {
      this.refs.thumb.removeEventListener(
        'pointerdown',
        this.onPointerDown,
        false
      )
      window.removeEventListener('pointermove', this.onPointerMove, false)
      window.removeEventListener('pointerup', this.onPointerUp, false)
    }

    window.removeEventListener('scroll', this.onScroll)
    window.removeEventListener('resize', this.onResize)
    window.visualViewport?.removeEventListener('resize', this.onResize)
    this.resizeObserver?.disconnect()
    this.resizeObserver = null
    this.cancelPendingFrames()
  }

  onScroll = () => {
    if (this.positionFrame !== null) {
      return
    }

    this.positionFrame = requestAnimationFrame(() => {
      this.positionFrame = null
      this.updateThumbPosition()
    })
  }

  onResize = () => {
    this.queueScrollbarRefresh()
  }

  observeScrollbarMetrics() {
    if (typeof ResizeObserver === 'undefined') {
      return
    }

    const targets = [this, document.documentElement, document.body]
    this.resizeObserver = new ResizeObserver(this.queueScrollbarRefresh)

    for (const target of targets) {
      if (!(target instanceof Element)) {
        continue
      }
      this.resizeObserver.observe(target)
    }
  }

  queueScrollbarRefresh = () => {
    if (this.metricsFrame !== null) {
      return
    }

    this.metricsFrame = requestAnimationFrame(() => {
      this.metricsFrame = null
      this.refreshScrollbarState()
    })
  }

  refreshScrollbarState() {
    this.metrics = this.getMetrics()
    this.applyScrollbarMetrics()
    this.updateThumbPosition()
  }

  applyScrollbarMetrics() {
    const thumb = this.refs.thumb
    const metrics = this.metrics

    if (!(thumb instanceof HTMLElement && metrics !== null)) {
      return
    }

    const disabled = metrics.limit <= 0
    if (this.lastDisabled !== disabled) {
      this.toggleAttribute('data-scrollbar-disabled', disabled)
      this.lastDisabled = disabled
    }

    if (this.lastThumbHeight === metrics.thumbHeight) {
      return
    }

    thumb.style.height = `${metrics.thumbHeight}px`
    this.lastThumbHeight = metrics.thumbHeight
  }

  updateThumbPosition() {
    const thumb = this.refs.thumb
    const metrics = this.metrics

    if (!(thumb instanceof HTMLElement && metrics !== null)) {
      return
    }

    const { limit, maxOffset } = metrics
    const progress = limit > 0 ? clamp(0, 1, window.scrollY / limit) : 0
    const thumbOffset = progress * maxOffset

    if (this.lastThumbOffset === thumbOffset) {
      return
    }

    thumb.style.transform = `translate3d(0,${thumbOffset}px,0)`
    this.lastThumbOffset = thumbOffset
  }

  onPointerMove = (e) => {
    const metrics = this.metrics
    const drag = this.drag

    if (drag === null || metrics === null) {
      return
    }
    e.preventDefault()

    const { maxOffset } = metrics
    const thumbOffset = clamp(
      0,
      maxOffset,
      e.clientY - drag.trackTop - drag.thumbOffset
    )
    const scroll = maxOffset > 0 ? (thumbOffset / maxOffset) * metrics.limit : 0

    window.scrollTo({ behavior: 'auto', top: scroll })
  }

  onPointerDown = (e) => {
    if (e.button !== 0) {
      return
    }

    const thumb = this.refs.thumb

    if (!(thumb instanceof HTMLElement)) {
      return
    }

    e.preventDefault()
    const metrics = this.getMetrics()
    if (metrics === null) {
      return
    }

    const trackTop = this.getBoundingClientRect().top

    this.metrics = metrics
    this.applyScrollbarMetrics()
    this.updateThumbPosition()
    this.drag = {
      thumbOffset: this.getThumbDragOffset(e.clientY, trackTop, metrics),
      trackTop
    }
    thumb.setPointerCapture(e.pointerId)
    document.documentElement.classList.add(GRABBING_CLASS)
  }

  onPointerUp = () => {
    this.drag = null
    document.documentElement.classList.remove(GRABBING_CLASS)
  }

  getMetrics() {
    const thumb = this.refs.thumb

    if (!(thumb instanceof HTMLElement)) {
      return null
    }

    const trackHeight = this.offsetHeight
    const scrollHeight = Math.max(
      document.documentElement.scrollHeight,
      window.innerHeight
    )
    const limit = Math.max(0, scrollHeight - window.innerHeight)
    const proportionalHeight = (window.innerHeight / scrollHeight) * trackHeight
    const thumbHeight = clamp(MIN_THUMB_HEIGHT, trackHeight, proportionalHeight)

    return {
      limit,
      maxOffset: Math.max(0, trackHeight - thumbHeight),
      thumbHeight
    }
  }

  getThumbDragOffset(clientY, trackTop, metrics) {
    const progress = metrics.limit > 0 ? window.scrollY / metrics.limit : 0
    const thumbTop = trackTop + progress * metrics.maxOffset
    return clientY - thumbTop
  }

  cancelPendingFrames() {
    if (this.positionFrame !== null) {
      cancelAnimationFrame(this.positionFrame)
      this.positionFrame = null
    }
    if (this.metricsFrame !== null) {
      cancelAnimationFrame(this.metricsFrame)
      this.metricsFrame = null
    }
  }
}

if (!customElements.get('ui-scrollbar')) {
  customElements.define('ui-scrollbar', ScrollbarElement)
}
